// src/components/SurveyDistributionDetails.jsx
import React, { useMemo, useState } from "react"
import { Card, CardBody, Row, Col, Badge } from "reactstrap"
import Knob from "../AllCharts/knob/knob"
import { formatDate } from "helpers/date-format"
import { useSelector } from "react-redux"

const SurveyDistributionDetails = ({ distribution, onClose }) => {
  const assignments = Array.isArray(distribution?.SurveyCustomerAssignments)
    ? distribution.SurveyCustomerAssignments
    : []

  const [activeAssignmentId, setActiveAssignmentId] = useState(
    assignments?.[0]?.id ?? null,
  )

  const mode = useSelector(state => state.Layout.layoutMode)
  const activeAssignment = useMemo(
    () => assignments.find(a => a?.id === activeAssignmentId) ?? null,
    [assignments, activeAssignmentId],
  )

  // Color tokens
  const BLUE = "#2990DF"
  const WHITE = "#FFFFFF"

  // Mode-based tokens
  const BG = mode === "dark" ? "#0B1220" : WHITE
  const TEXT = mode === "dark" ? WHITE : "#0B1220"
  const BORDER = mode === "dark" ? "#1F2937" : "#E5E7EB"
  const SUBTEXT = mode === "dark" ? "#E5E7EB" : "#6B7280"
  const BG_SOFT = mode === "dark" ? "#1E293B" : "#F9FAFB"

  const chip = ok => ({
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    color: ok ? BLUE : TEXT,
    background: ok ? (mode === "dark" ? "#1E3A8A" : "#E0F2FE") : "transparent",
  })

  const questionTypeColor = type =>
    type === "rating" ? BLUE : type === "multiple choice" ? BLUE : BLUE

  return (
    <Card
      className="shadow-sm mt-4 br"
      style={{
        background: mode === "dark" ? "#252F40" : "#fff",
        color: TEXT,
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
      }}
    >
      <CardBody style={{ padding: 20 }}>
        <Row className="gx-3 gy-3">
          {/* LEFT PANEL */}
          <Col
            xs="12"
            md="4"
            className="pt-0 pb-0"
            style={{
              borderRight: `1px solid ${BORDER}`,
            }}
          >
            <h5 style={{ marginBottom: 16, color: TEXT, fontWeight: 600 }}>
              Survey Participants
            </h5>

            <div
              role="list"
              style={{
                maxHeight: 480,
                overflowY: "auto",
                paddingRight: 6,
              }}
            >
              {assignments.length === 0 && (
                <div
                  style={{
                    color: SUBTEXT,
                    textAlign: "center",
                    padding: 24,
                  }}
                >
                  No participants.
                </div>
              )}

              {assignments.map(a => {
                const id = a?.id
                const active = id === activeAssignmentId
                const sentiment = Number(a?.sentimentScore ?? 0)
                const fgColor =
                  sentiment >= 7
                    ? "#28a745"
                    : sentiment >= 4
                      ? "#f4a261"
                      : "#e63946"

                const fullName =
                  `${a?.Customer?.firstName ?? ""} ${
                    a?.Customer?.lastName ?? ""
                  }`.trim() || "Unknown customer"

                return (
                  <button
                    key={id}
                    onClick={() => setActiveAssignmentId(id)}
                    style={{
                      width: "100%",
                      display: "flex",
                      alignItems: "center",
                      padding: "10px 12px",
                      marginBottom: 8,
                      borderRadius: 10,
                      background: active
                        ? mode === "dark"
                          ? "#1E3A8A"
                          : "#E0F2FE"
                        : BG_SOFT,
                      border: `1px solid ${active ? BLUE : BORDER}`,
                      cursor: "pointer",
                      textAlign: "left",
                      color: TEXT,
                    }}
                  >
                    <Knob
                      value={sentiment}
                      max={10}
                      fgColor={fgColor}
                      thickness={0.22}
                      height={36}
                      width={36}
                      readOnly
                    />
                    <div style={{ flex: 1, marginLeft: 10 }}>
                      <div
                        style={{
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          color: TEXT,
                        }}
                      >
                        {fullName}
                      </div>
                      <div style={{ marginTop: 6, display: "flex", gap: 8 }}>
                        <span style={chip(!!a?.Opened)}>
                          {a?.Opened ? "Opened" : "Not opened"}
                        </span>
                        <span style={chip(!!a?.Responded)}>
                          {a?.Responded ? "Responded" : "No response"}
                        </span>
                      </div>
                      <div
                        style={{
                          marginTop: 6,
                          color: SUBTEXT,
                          fontSize: 12,
                        }}
                      >
                        Opened:{" "}
                        {a?.openedDate ? formatDate(a.openedDate) : "N/A"} ·
                        Responded:{" "}
                        {a?.respondedDate ? formatDate(a.respondedDate) : "N/A"}
                      </div>
                    </div>
                    <div
                      style={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: TEXT,
                        marginLeft: 6,
                      }}
                    >
                      {sentiment} / 10
                    </div>
                  </button>
                )
              })}
            </div>
          </Col>

          {/* RIGHT PANEL */}
          <Col xs="12" md="8" className="pt-0">
            <h5 style={{ marginBottom: 16, color: TEXT, fontWeight: 600 }}>
              Survey Responses
            </h5>

            {!activeAssignment && (
              <div
                style={{
                  color: SUBTEXT,
                  textAlign: "center",
                  padding: 24,
                }}
              >
                Select a participant to view responses.
              </div>
            )}

            {activeAssignment?.Survey?.SurveyCategories?.length ? (
              activeAssignment.Survey.SurveyCategories.map(cat => (
                <Card
                className="br"
                  key={cat?.id}
                  style={{
                    background: "transparent",
                    marginBottom: 12,
                  }}
                >
                  <div
                    style={{
                      padding: 12,
                      borderBottom: `1px solid ${BORDER}`,
                      color: TEXT,
                      fontWeight: 600,
                    }}
                  >
                    {cat?.name}
                  </div>

                  <div style={{ padding: 12 }}>
                    {cat?.SurveyQuestions?.map((q, idx) => {
                      const qType = q?.questionType || "text"
                      const response = q?.SurveyResponses?.find(
                        r => r?.customerId === activeAssignment?.Customer?.id,
                      )
                      const selected = response?.responseValue ?? "No response"
                      const isMCQ = qType === "multiple choice"

                      return (
                        <div
                          key={`${cat?.id}-${idx}`}
                          style={{
                            border: `1px solid ${BORDER}`,
                            borderRadius: 10,
                            padding: 10,
                            marginBottom: 10,
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              marginBottom: 8,
                            }}
                          >
                            <span
                              style={{
                                fontWeight: 600,
                                color: TEXT,
                              }}
                            >
                              {q?.questionText}
                            </span>
                            <Badge
                              pill
                              style={{
                                background: BLUE,
                                color: WHITE,
                                fontSize: 10,
                              }}
                            >
                              {qType.toUpperCase()}
                            </Badge>
                          </div>

                          {isMCQ ? (
                            <Row style={{ marginTop: 4 }}>
                              {(q?.options || []).map((opt, i) => {
                                const active = opt === selected
                                return (
                                  <Col xs="auto" key={i} style={{ margin: 4 }}>
                                    <div
                                      style={{
                                        minWidth: 90,
                                        padding: "6px 10px",
                                        borderRadius: 8,
                                        textAlign: "center",
                                        color: active ? WHITE : TEXT,
                                        fontWeight: 500,
                                      }}
                                    >
                                      {opt}
                                    </div>
                                  </Col>
                                )
                              })}
                            </Row>
                          ) : (
                            <div style={{ marginTop: 6 }}>
                              <span style={{ fontWeight: 600, color: TEXT }}>
                                Response:
                              </span>{" "}
                              {selected !== "No response" ? (
                                <span>{selected}</span>
                              ) : (
                                <span style={{ color: SUBTEXT }}>
                                  No response
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </Card>
              ))
            ) : (
              <div
                style={{
                  color: SUBTEXT,
                  textAlign: "center",
                  padding: 24,
                }}
              >
                No survey responses available.
              </div>
            )}
          </Col>
        </Row>

        {/* Footer */}
        <div
          style={{ display: "flex", justifyContent: "flex-end", marginTop: 16 }}
        >
          <button
            onClick={onClose}
            style={{
              background: BLUE,
              color: WHITE,
              border: "none",
              borderRadius: 8,
              padding: "8px 14px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </div>
      </CardBody>
    </Card>
  )
}

export default SurveyDistributionDetails
